import React, { useMemo, useRef, useCallback, useState } from 'react';

import { AxiosError } from 'axios';
import { debounce } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useOutsideHandler } from 'src/common/hooks';
import { fetchCampaignDetails } from 'src/tcr/api';
import { CampaignDetailType } from 'src/tcr/types';

const SearchCampaignDetails: React.FC = () => {
  const searchRef = useRef(null);
  const menuRef = useRef(null);
  const [searchError, setSearchError] = useState('');
  const [filteredCampaignDetails, setFilteredCampaignDetails] = useState<CampaignDetailType[]>([]);
  const { setValue } = useFormContext();

  const fetch: (value: string) => void = async (value) => {
    try {
      const response = await fetchCampaignDetails(value);
      setFilteredCampaignDetails(response.results);
    } catch (err) {
      const error = err as AxiosError<{ detail: string }>;
      const detail = error?.response?.data?.detail;
      setSearchError(detail || 'An unknown error has occurred');
      setFilteredCampaignDetails([]);
    }
  };

  const debounceHandler = useMemo(() => debounce(fetch, 1000), []);

  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSearchError('');
      const text = event.target.value;
      if (!text) {
        setFilteredCampaignDetails([]);
        return;
      }
      debounceHandler(text);
    },
    [debounceHandler]
  );

  const handleOnBlur = (): void => {
    setFilteredCampaignDetails([]);
  };

  const handleSetCampaignDetail = (campaign_detail: CampaignDetailType) => () => {
    setFilteredCampaignDetails([]);
    setValue('campaign_detail', campaign_detail.id);
  };

  useOutsideHandler(menuRef, handleOnBlur);

  return (
    <div className="relative" ref={menuRef}>
      <label htmlFor="search-campaign-detail" className="input-label">
        Search for Campaign Detail
        <input
          name="search-campaign-detail"
          ref={searchRef}
          className="input w-full mt-1 transition duration-150 ease-in-out"
          placeholder="Search by name"
          data-testid="campaign-detail-search"
          onChange={handleOnChange}
          type="text"
          autoComplete="off"
        />
        {searchError && <p className="error-text">{searchError}</p>}
      </label>
      <div
        className="absolute left-0 bg-white-0.0 min-w-96 mt-2 origin-top-right rounded shadow-lg z-50 border border-white-1.0 max-h-80 overflow-auto"
        hidden={filteredCampaignDetails.length === 0}
      >
        {filteredCampaignDetails.map((campaign_detail) => (
          <div
            key={campaign_detail.id}
            className="p-4 border-b border-white-1.0 hover:bg-white-1.0 cursor-pointer"
            role="presentation"
            onClick={handleSetCampaignDetail(campaign_detail)}
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            data-testid={`campaign-detail-${campaign_detail.id}`}
          >
            {campaign_detail.name}
          </div>
        ))}
      </div>
    </div>
  );
};

export default SearchCampaignDetails;
